import { FormCard, FormItem, Heading, TextArea } from '@lmc/ui-kit'
import { Button, Text } from '@lmc/ui-kit/src/general'
import Divider from '@lmc/ui-kit/src/layout/Divider/Divider'
import { Form, Modal } from 'antd'
import { showToast } from 'common/utils'
import { NAMESPACE } from 'locale'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { useResolveDiscrepancy } from '../../hooks/useResolveDiscrepancy'

type ResolveDiscrepancyModalProps = {
  open: boolean
  stockRecordId: string
  medicationName?: string
  onCancel: () => void
  onSuccess?: () => void
}

const ResolveDiscrepancyModal = ({
  open,
  stockRecordId,
  medicationName,
  onCancel,
  onSuccess
}: ResolveDiscrepancyModalProps) => {
  const { t } = useTranslation(NAMESPACE.EMAR_V2)
  const [form] = Form.useForm()

  const { resolveDiscrepancy, isLoading } = useResolveDiscrepancy({
    medicationName,
    stockRecordId,
    onSuccess: () => {
      showToast({
        type: 'success',
        title: t('DISCREPANCY_RESOLVED_SUCCESS')
      })
      form.resetFields()
      onSuccess?.()
    },
    onError: (error) => {
      showToast({
        type: 'error',
        title: t('DISCREPANCY_RESOLVED_ERROR')
      })
      console.error('Error resolving discrepancy:', error)
    }
  })

  const handleSave = async () => {
    try {
      const values = await form.validateFields()
      await resolveDiscrepancy({
        stockRecordId,
        reason: values.reason
      })
    } catch (error) {
      // Validation failed
    }
  }

  const handleCancel = () => {
    form.resetFields()
    onCancel()
  }

  return (
    <Modal
      open={open}
      onCancel={handleCancel}
      footer={null}
      width={648}
      title={
        <ModalTitle>
          <Heading level={3}>{t('RESOLVE_DISCREPANCY')}</Heading>
        </ModalTitle>
      }
    >
      <Divider />

      <Form form={form} preserve={false}>
        <ModalContentContainer>
          <InstructionText>
            {t('ADD_REASON_TO_RESOLVE_DISCREPANCY')}
          </InstructionText>

          <StyledFormCard level={2}>
            <FormItem
              name="reason"
              label={<Text>{t('REASON_FOR_DISCREPANCY')}</Text>}
              required
              full
              rules={[
                {
                  required: true,
                  message: t('REASON_FOR_DISCREPANCY_REQUIRED')
                },
                { max: 150, message: t('DESCRIPTION_MAX_LENGTH') }
              ]}
            >
              <StyledTextArea
                rows={4}
                placeholder={t('TYPE_HERE')}
                maxLength={150}
                showCount
                data-testid="resolve-discrepancy-reason"
              />
            </FormItem>
          </StyledFormCard>
        </ModalContentContainer>
      </Form>

      <Divider />

      <ButtonsContainer>
        <Button onClick={handleCancel} data-testid="cancel-btn">
          {t('CANCEL')}
        </Button>
        <Button
          variant="primary"
          onClick={handleSave}
          disabled={isLoading}
          isLoading={isLoading}
          data-testid="save-btn"
        >
          {t('SAVE')}
        </Button>
      </ButtonsContainer>
    </Modal>
  )
}

const StyledFormCard = styled(FormCard)`
  padding-bottom: 32px;
`

const ModalTitle = styled.div`
  text-transform: none;
  font-weight: normal;
`

const ModalContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: 24px 0;
`

const InstructionText = styled(Text)`
  color: ${({ theme }) => theme.color.neutral.color4};
`

const StyledTextArea = styled(TextArea)`
  resize: none;
`

const ButtonsContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding-top: 16px;
`

export default ResolveDiscrepancyModal
